export interface PasswordStrength {
  score: number; // 0-4 (0 = very weak, 4 = very strong)
  feedback: string[];
  color: string;
  label: string;
  percentage: number;
}

export function calculatePasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return {
      score: 0,
      feedback: ['Enter a password'],
      color: 'bg-gray-300',
      label: 'No password',
      percentage: 0,
    };
  }

  let score = 0;
  const feedback: string[] = [];

  // Length checks
  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('At least 8 characters');
  }

  if (password.length >= 12) {
    score += 1;
  } else if (password.length >= 8) {
    feedback.push('Consider using 12+ characters for better security');
  }

  // Character variety checks
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add lowercase letters');
  }

  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add uppercase letters');
  }

  if (/[0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add numbers');
  }

  if (/[^A-Za-z0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add special characters (!@#$%^&*)');
  }

  // Advanced checks
  if (password.length >= 16) {
    score += 1;
  }

  // Only give bonus for no repetition if password has all character types
  const hasAllTypes = /[a-z]/.test(password) && /[A-Z]/.test(password) && 
                      /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password);
  
  if (hasAllTypes && !/(.)\1{2,}/.test(password)) {
    score += 1; // No repeated characters
  } else if (password.length >= 8 && /(.)\1{2,}/.test(password)) {
    feedback.push('Avoid repeating characters');
  }

  // Cap score at 4 and calculate metrics
  const finalScore = Math.min(score, 4);
  const percentage = (finalScore / 4) * 100;

  // Determine color and label based on score
  let color: string;
  let label: string;

  switch (finalScore) {
    case 0:
    case 1:
      color = 'bg-red-500';
      label = 'Very Weak';
      break;
    case 2:
      color = 'bg-orange-500';
      label = 'Weak';
      break;
    case 3:
      color = 'bg-yellow-500';
      label = 'Good';
      break;
    case 4:
      color = 'bg-green-500';
      label = 'Strong';
      break;
    default:
      color = 'bg-gray-300';
      label = 'Unknown';
  }

  return {
    score: finalScore,
    feedback: feedback.length > 0 ? feedback : ['Password looks strong!'],
    color,
    label,
    percentage,
  };
}